import React, { Component } from 'react'
import PropTypes from 'prop-types'
import IPropTypes from 'react-immutable-proptypes'
import { FormattedMessage } from 'react-intl'
import OutsideClickHandler from 'react-outside-click-handler'
import Tooltip, { POSITION, ALIGN } from 'gnos-tooltip'
import _ from 'lodash'
import cx from 'classnames'
import log from 'loglevel'

import config from '../../../../config'

import { EntityRecord } from '../../../../global/records'

import Button from './components/Button'
import DeskWrap from './components/DeskWrap'

import hocConnect from './hocConnect'
import { LOCALE_PREFIX } from './constants'

import { getCookie } from './helpers'

import './style.scss'

// get components by config (every module has array of toolbar desks components)
// component key must be same as directory of toolbar desk component eg:
// key "exports" for module "report" must be in path "./scenes/Gnosus/scenes/Report/components/Toolbar/components/ToolbarDesk/Exports"
const toolbarDeskComponents = _.reduce(
    config.modules,
    (acc, module) => {
        if (!module.toolbar) return acc

        const upperModuleKey = _.upperFirst(module.key)
        const components = _.reduce(
            _.get(module, 'toolbar.components', []),
            (accComp, componentKey) => {
                const upperComponentKey = _.upperFirst(componentKey)

                try {
                    const component =
                        require(`../../scenes/${upperModuleKey}/components/Toolbar/components/ToolbarDesks/${upperComponentKey}`).default // eslint-disable-line import/no-dynamic-require, global-require
                    return { ...accComp, [componentKey]: component }
                } catch (error) {
                    log.warn(
                        `Missing ToolbarDesk component ${_.upperFirst(componentKey)} for module ${_.upperFirst(
                            module.key
                        )}`
                    )
                }

                return accComp
            },
            {}
        )

        return { ...acc, [module.key]: components }
    },
    {}
)

@hocConnect
export default class Toolbar extends Component {
    static propTypes = {
        addToFavourites: PropTypes.func.isRequired,
        desks: IPropTypes.map.isRequired,
        entity: IPropTypes.recordOf(EntityRecord),
        favourites: IPropTypes.list,
        isActive: PropTypes.bool.isRequired,
        module: PropTypes.string.isRequired,
        removeFromFavourites: PropTypes.func.isRequired,
        show: PropTypes.bool.isRequired,
        tabId: PropTypes.string.isRequired,
        toolbarIconClick: PropTypes.func.isRequired,
        toolbarOutsideClick: PropTypes.func.isRequired,
        uniqueId: PropTypes.string.isRequired,
        showReportIndicator: PropTypes.string,
    }

    shouldComponentUpdate(nextProps) {
        if (this.props.tabId !== nextProps.tabId) return true
        if (this.props.uniqueId !== nextProps.uniqueId) return true
        if (this.props.isActive !== nextProps.isActive) return true
        if (this.props.module !== nextProps.module) return true
        if (this.props.show !== nextProps.show) return true
        if (this.props.entity && !this.props.entity.equals(_.get(nextProps, 'entity', null))) return true
        if (this.props.desks && !this.props.desks.equals(_.get(nextProps, 'desks', null))) return true
        if (this.props.favourites && !this.props.favourites.equals(_.get(nextProps, 'favourites', null))) return true
        if (this.props.showReportIndicator !== nextProps.showReportIndicator) return true

        return false
    }

    onButtonClickHandler = (componentName) => {
        const { toolbarIconClick, uniqueId, module, favourites, entity, addToFavourites, removeFromFavourites } =
            this.props

        if (componentName === 'favourite' && favourites && entity) {
            const isInFavourites = favourites.find((x) => _.toNumber(x.get('entId', null)) === entity.get('id', null))

            if (isInFavourites) {
                removeFromFavourites(entity.get('id', null))
                return null
            }

            addToFavourites(entity.get('id', null))
            return null
        }

        toolbarIconClick(componentName, uniqueId, module)
        return null
    }

    onOutsideClickHandler = () => {
        const { uniqueId, module, toolbarOutsideClick } = this.props

        toolbarOutsideClick(uniqueId, module)
        return null
    }

    renderToolbarDesks = () => {
        const { desks, module, tabId } = this.props

        const desksComponents = _.map(_.get(config, `modules.${module}.toolbar.components`, []), (name) => {
            if (!desks || !desks.get(name, false)) return null

            const DeskComponent = _.get(toolbarDeskComponents, `${module}.${name}`, null)
            return (
                <DeskWrap key={name}>
                    <DeskComponent module={module} id={tabId} />
                </DeskWrap>
            )
        })

        return <div className={'desks'}>{desksComponents}</div>
    }

    renderTools = () => {
        const { desks, module, entity, favourites } = this.props

        const buttons = _.map(toolbarDeskComponents[module], (component, name) => {
            if (name === 'favourite' && getCookie('sessionMode') === 'permanent') return null
            if (name === 'favourite' && favourites && entity) {
                const isActive = favourites.find((x) => _.toNumber(x.get('entId', null)) === entity.get('id', null))

                return (
                    <Tooltip
                        id={`toolbar-${name}-tooltip`}
                        key={name}
                        content={
                            <FormattedMessage id={`${LOCALE_PREFIX}.tooltip.${name}`} values={{ isFav: !!isActive }} />
                        }
                        showDelay={1500}
                        width={'400px'}
                        position={POSITION.BOTTOM}
                        align={ALIGN.END}
                        doubleWrap
                    >
                        <div>
                            <Button
                                key={name}
                                name={name}
                                active={!!isActive}
                                onClickHandler={() => this.onButtonClickHandler(name)}
                            />
                        </div>
                    </Tooltip>
                )
            }

            const isActive = desks ? desks.get(name, false) : false

            return (
                <Tooltip
                    id={`toolbar-${name}-tooltip`}
                    key={name}
                    content={
                        <FormattedMessage id={`${LOCALE_PREFIX}.tooltip.${name}`} values={{ isFav: !!isActive }} />
                    }
                    showDelay={1500}
                    width={'400px'}
                    position={POSITION.BOTTOM}
                    align={ALIGN.END}
                    doubleWrap
                >
                    <div>
                        <Button
                            key={name}
                            name={name}
                            active={isActive}
                            onClickHandler={() => this.onButtonClickHandler(name)}
                        />
                    </div>
                </Tooltip>
            )
        })

        return <div className={'tools'}>{buttons}</div>
    }

    render() {
        const { isActive, show, showReportIndicator } = this.props

        const showReport = showReportIndicator !== 'N'

        return (
            <OutsideClickHandler
                key={'toolbar-outside-click-handler'}
                onOutsideClick={this.onOutsideClickHandler}
                disabled={!isActive || !show}
            >
                <div className={cx('toolbar', (!show || !isActive) && 'hide')}>
                    {this.renderToolbarDesks()}
                    {showReport && this.renderTools()}
                </div>
            </OutsideClickHandler>
        )
    }
}
